import type { ReactNode } from 'react'

import { Breadcrumb, Typography } from 'antd'
import cx from 'classnames'
import { useLocation, Link } from 'react-router-dom'

import { navigation } from '../../layout/home/components/menu'
import type { Route, RouteWithChildren } from '../../layout/home/components/types'

const { Title, Text } = Typography

interface NavigationMatch {
  title: string
  icon?: ReactNode
  to?: string
}

export interface BreadcrumbItem {
  key: string
  label: ReactNode
  to?: string
  className?: string
}

export interface PageHeaderProps {
  title?: ReactNode
  description?: ReactNode
  icon?: ReactNode
  hideIcon?: boolean
  suffix?: ReactNode
  breadcrumbs?: BreadcrumbItem[]
  breadcrumbSeparator?: ReactNode
  className?: string
  titleClassName?: string
  descriptionClassName?: string
  breadcrumbClassName?: string
}

function isRouteWithChildren(route: Route): route is RouteWithChildren {
  return 'children' in route
}

function matchesPath(pathname: string, candidate?: string): boolean {
  if (!candidate) {
    return false
  }

  if (candidate === '/') {
    return pathname === '/'
  }

  return pathname === candidate || pathname.startsWith(`${candidate}/`)
}

function collectMatches(
  pathname: string,
  routes: Route[],
  trail: NavigationMatch[] = []
): NavigationMatch[] {
  for (const route of routes) {
    const entries: string[] = []
    if ('to' in route && route.to) {
      entries.push(route.to)
    }
    if ('matchPaths' in route && route.matchPaths?.length) {
      entries.push(...route.matchPaths)
    }

    const matches = entries.some(entry => matchesPath(pathname, entry))

    if (isRouteWithChildren(route)) {
      // First check if any child matches
      const childMatch = collectMatches(pathname, route.children, trail)

      if (childMatch.length > 0) {
        // If child matched, prepend parent to the trail
        return [{ title: route.title, icon: route.icon, to: route.to }, ...childMatch]
      }

      // If no child matched but parent itself matches, add parent
      if (matches) {
        return [...trail, { title: route.title, icon: route.icon, to: route.to }]
      }
    } else if (matches) {
      return [...trail, { title: route.title, icon: route.icon, to: route.to }]
    }
  }

  return []
}

export function PageHeader({
  title,
  description,
  icon,
  hideIcon = false,
  suffix,
  breadcrumbs,
  breadcrumbSeparator = <span className='text-slate-400 px-1'>/</span>,
  className,
  titleClassName,
  descriptionClassName,
  breadcrumbClassName
}: PageHeaderProps): JSX.Element {
  const { pathname } = useLocation()

  const navigationTrail = collectMatches(pathname, navigation)
  const lastMatch = navigationTrail.at(-1)

  const computedTitle = title ?? lastMatch?.title ?? 'Untitled page'
  const computedIcon = icon ?? lastMatch?.icon

  const computedBreadcrumbs: BreadcrumbItem[] =
    breadcrumbs ??
    navigationTrail.map((match, index) => ({
      key: match.to ?? `${match.title}-${index}`,
      label: match.title,
      to: index === navigationTrail.length - 1 ? undefined : match.to
    }))

  return (
    <div className={cx('flex flex-col gap-1', className)}>
      <div className='flex items-center gap-3'>
        {!hideIcon && computedIcon ? (
          <span className='text-2xl text-slate-600'>{computedIcon}</span>
        ) : null}
        <Title level={3} className={cx('!mb-0', titleClassName)}>
          {computedTitle}
        </Title>
        {suffix ?? null}
      </div>
      {description ? (
        <Text type='secondary' className={descriptionClassName}>
          {description}
        </Text>
      ) : null}
      {computedBreadcrumbs.length > 1 ? (
        <Breadcrumb className={breadcrumbClassName} separator={breadcrumbSeparator}>
          {computedBreadcrumbs.map(item => (
            <Breadcrumb.Item key={item.key} className={item.className}>
              {item.to ? <Link to={item.to}>{item.label}</Link> : item.label}
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      ) : null}
    </div>
  )
}
